import { FaArrowLeft } from "react-icons/fa6";
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";

import { TransactionDetailModalInput } from "./types";
import Modal from "../../components/Modal";
import DataUpLogo from "../../assets/images/DataUpLogo.svg";

export const TransactionDetailsModal = ({
  isOpen,
  onClose,
  transaction,
  formatAmountWithSymbol,
  formatDate,
  formatTime,
  getCurrencySymbol,
}: TransactionDetailModalInput) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: `Receipt_${transaction?.id || "Transaction"}`,
  });
  const getRecipientLabel = (type: string) => {
    const labels: Record<string, string> = {
      CABLE: "Smartcard No:",
      DATA: "Phone No:",
      AIRTIME: "Phone No:",
      TOPUP: "Phone No:",
      GAMECARD: "Email Address:",
      GIFTCARD: "Email Address:",
      "DG COINS PURCHASE": "Ref No:",
    };

    return labels[type] || "Account/Ref No";
  };
  if (!transaction) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-lg p-0">
      <div ref={contentRef} className="print-container">
        <div className="flex flex-col items-center justify-center pt-8 pb-4 border-b border-dashed dark:border-gray-700">
          <img
            src={DataUpLogo}
            alt="DataUp Logo"
            className="h-10 w-auto object-contain mb-2"
          />
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-bold">
            Official Transaction Receipt
          </p>
        </div>
        <div className="flex items-center p-4 border-b dark:border-gray-700 no-print">
          <button onClick={onClose} className="mr-2">
            <FaArrowLeft
              size={20}
              className="text-gray-600 dark:text-gray-300"
            />
          </button>
          <h2 className="text-lg font-semibold">Transaction Details</h2>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl mt-4 mb-6 space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Transaction Status
            </p>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                transaction.status_text === "SUCCESS"
                  ? "bg-green-100 text-green-700"
                  : transaction.status_text === "PENDING"
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-red-100 text-red-700"
              }`}
            >
              {transaction.status_text}
            </span>
          </div>

          {transaction.unique_number && (
            <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {getRecipientLabel(transaction.type_text)}
              </p>
              <p className="font-semibold text-sm dark:text-white truncate ml-4">
                {transaction.unique_number}
              </p>
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-lg font-semibold dark:text-white">
                Order Details
              </h3>
              <div className="flex p-y-2">
                <p className="text-gray-500">Order ID: </p>
                <p className="font-medium dark:text-white ml-2">
                  #{transaction.id}
                </p>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(transaction.created_at)} {"/"}{" "}
                {formatTime(transaction.created_at)}
              </p>
            </div>
            {/* Order Type Badge */}
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 uppercase">
              {transaction.type_text}
            </span>
          </div>

          {/* Transaction items */}
          <div className="space-y-4 mb-8">
            {transaction.items_details &&
              transaction.items_details.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between border-b pb-4 dark:border-gray-700"
                >
                  <div className="flex items-center gap-3">
                    {/* Visual Indicator: Image or Icon */}
                    <div className="relative">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          crossOrigin="anonymous"
                          className="w-11 h-11 rounded-lg object-cover border dark:border-gray-600"
                        />
                      ) : (
                        <div className="w-11 h-11 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-500">
                          <span className="text-xl">💳</span>
                        </div>
                      )}
                    </div>

                    {/* Item & User Detail */}
                    <div>
                      <p className="text-sm font-bold text-gray-800 dark:text-white">
                        {item.name || transaction.type_text}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded font-medium uppercase">
                          User
                        </span>
                        <span className="text-xs text-gray-600 dark:text-gray-400 font-bold">
                          {item.username || "Guest User"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Pricing Column */}
                  <div className="text-right">
                    <p className="text-[11px] text-blue-500 font-semibold">
                      {item.currency_amount || ""}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {item.price}
                    </p>
                  </div>
                </div>
              ))}

            {/* If no item details are provided, show placeholder */}
            {(!transaction.items_details ||
              transaction.items_details.length === 0) && (
              <div className="py-4 text-center text-gray-500">
                No detailed item information available
              </div>
            )}
          </div>

          {/* Transaction summary */}
          <div className="space-y-2 border-t pt-4 dark:border-gray-700">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
              <span className="text-gray-500 dark:text-gray-400">
                {" "}
                {formatAmountWithSymbol(
                  transaction.subtotal ??
                    transaction.price ??
                    transaction.amount,
                  getCurrencySymbol(transaction.currency_id) ||
                    transaction.currency_symbol ||
                    transaction.currency?.symbol ||
                    "",
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Fee</span>
              <span className="text-gray-500 dark:text-gray-400">
                {getCurrencySymbol(transaction.currency_id)}
                {transaction.fee || "0"}
              </span>
            </div>
            <div className="flex justify-between border-t pt-2 mt-2 dark:border-gray-700">
              <span className="font-semibold text-gray-500 dark:text-gray-400">
                Total
              </span>
              <div className="flex items-center">
                {transaction.coin_amount && (
                  <span className="text-yellow-500 mr-2">
                    🪙 {transaction.coin_amount}
                  </span>
                )}
                <span className="font-semibold text-gray-500 dark:text-gray-400">
                  {formatAmountWithSymbol(
                    transaction.price ?? transaction.amount,
                    getCurrencySymbol(transaction.currency_id) ||
                      transaction.currency_symbol ||
                      transaction.currency?.symbol ||
                      "",
                  )}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-center mt-6 no-print">
              <button
                onClick={() => handlePrint()}
                className="bg-pry-500 rounded-md lg:text-lg p-2 px-6 text-white font-semibold hover:bg-pry-600 transition-colors"
              >
                Print Receipt
              </button>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-container { 
            padding: 20px; 
            color: black !important; 
            background: white !important; 
          }
          * { color-adjust: exact; -webkit-print-color-adjust: exact; }
        }
      `}</style>
    </Modal>
  );
};
